import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listDevices, getDevice, getTelemetry, getOverview,
  listAlerts, resolveAlert, listRules, createRule, deleteRule, rotateAPIKey, login, register,
  setToken,
} from './client'
import type { RegisterRequest, CreateRuleRequest, AuthResponse } from './client'

// ── Overview ──────────────────────────────────────────────────────────────────

export function useOverview() {
  return useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    refetchInterval: 30_000,
  })
}

// ── Devices ───────────────────────────────────────────────────────────────────

export function useDevices() {
  return useQuery({
    queryKey: ['devices'],
    queryFn: listDevices,
    refetchInterval: 30_000,
  })
}

export function useDevice(id: number) {
  return useQuery({
    queryKey: ['device', id],
    queryFn: () => getDevice(id),
    enabled: id > 0,
  })
}

export function useTelemetry(deviceId: number, metric: string, since: string) {
  return useQuery({
    queryKey: ['telemetry', deviceId, metric, since],
    queryFn: () => getTelemetry(deviceId, metric, since),
    enabled: deviceId > 0 && metric !== '',
    refetchInterval: 60_000,
  })
}

// ── Alerts ────────────────────────────────────────────────────────────────────

export function useAlerts() {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: listAlerts,
    refetchInterval: 30_000,
  })
}

export function useResolveAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => resolveAlert(id),
    onSuccess() {
      qc.invalidateQueries({ queryKey: ['alerts'] })
      qc.invalidateQueries({ queryKey: ['overview'] })
    },
  })
}

// ── Alert Rules ───────────────────────────────────────────────────────────────

export function useRules() {
  return useQuery({
    queryKey: ['rules'],
    queryFn: listRules,
  })
}

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateRuleRequest) => createRule(req),
    onSuccess() { qc.invalidateQueries({ queryKey: ['rules'] }) },
  })
}

export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteRule(id),
    onSuccess() { qc.invalidateQueries({ queryKey: ['rules'] }) },
  })
}

// ── Auth mutations ────────────────────────────────────────────────────────────

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      login(email, password),
    onSuccess(data: AuthResponse) {
      setToken(data.token)
      qc.invalidateQueries()
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: RegisterRequest) => register(req),
    onSuccess(data: AuthResponse) {
      setToken(data.token)
      qc.invalidateQueries()
    },
  })
}

// ── Key rotation ──────────────────────────────────────────────────────────────

export function useRotateAPIKey() {
  return useMutation({ mutationFn: rotateAPIKey })
}
